import { useState } from 'react';
import type { PositionDelta } from '@view-model/models/common/types/ui';
import { ResizerCircleElement } from '@view-model/models/common/components/RectResizer/ResizerCircleElement';
import { resizeKeepingAspectRatio, type ResizeCorner } from '../domain/FileContainerImageSize';
import type { Size } from '@view-model/models/common/basic';

const Corners: { corner: ResizeCorner; cursor: 'nesw-resize' | 'nwse-resize' }[] = [
  { corner: 'left-top', cursor: 'nwse-resize' },
  { corner: 'right-top', cursor: 'nesw-resize' },
  { corner: 'left-bottom', cursor: 'nesw-resize' },
  { corner: 'right-bottom', cursor: 'nwse-resize' },
];

// 共同編集で size がドラッグ中に変わっても計算がずれないよう、開始時の寸法を基準に保持する。
type Dragging = { corner: ResizeCorner; startSize: Size; width: number; height: number };

type Props = {
  size: Size;
  onResizeEnd(positionDelta: PositionDelta, size: Size): void;
};

export const FileContainerImageResizer: React.FC<Props> = ({ size, onResizeEnd }: Props) => {
  const [dragging, setDragging] = useState<Dragging | null>(null);
  const resized = dragging
    ? resizeKeepingAspectRatio(dragging.startSize, dragging.corner, dragging.width, dragging.height)
    : null;
  const { dx, dy } = resized?.positionDelta ?? { dx: 0, dy: 0 };
  const { width, height } = resized?.size ?? size;

  const cornerPosition = (corner: ResizeCorner) => ({
    x: corner.startsWith('left') ? dx : dx + width,
    y: corner.endsWith('top') ? dy : dy + height,
  });

  return (
    <>
      {resized && <rect x={dx} y={dy} width={width} height={height} fill="#eee" fillOpacity={0.7} stroke="#3b82f6" />}
      {Corners.map(({ corner, cursor }) => (
        <ResizerCircleElement
          key={corner}
          cursor={cursor}
          position={cornerPosition(corner)}
          circleRadius={12}
          handlePosition={corner}
          onResizeStart={() => setDragging({ corner, startSize: size, width: size.width, height: size.height })}
          onResize={(_dx, _dy, dWidth, dHeight) =>
            setDragging((current) =>
              current ? { ...current, width: current.width + dWidth, height: current.height + dHeight } : current
            )
          }
          onResizeEnd={() => {
            if (dragging && resized && !resized.size.isEqual(dragging.startSize)) {
              onResizeEnd(resized.positionDelta, resized.size);
            }
            setDragging(null);
          }}
        />
      ))}
    </>
  );
};
